import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router-dom';

import type { ProposedChangeWorksheetRow } from 'components/Collaboration/utils/collaboration-types';
import {
  findApprovalDefinitionStep,
  rejectPathReturnsToStep,
} from 'components/SPM/PlanDetailView/utils/buildWorkflowRunSteps';
import { PlanScenarioApprovalParams } from 'constants/routes';
import { useIsFeatureEnabled } from 'contexts/FeatureFlags';
import { TQ_ACTIONS } from 'contexts/Permissions/constants';
import { usePermissions } from 'contexts/Permissions/Permissions';
import { useSPMPlanContext } from 'contexts/SPMPlanContext';
import { UsersService } from 'services';
import { ProposedChangeGroupsServiceV2 } from 'services/ProposedChangeGroupsServiceV2';
import { SPMApprovalsService } from 'services/spmApprovals';
import type { StringifiedUUID } from 'types';
import { ProposedChangeGroupStatus } from 'types/SPM';
import type { SPMApproval, SPMApprovalGroup } from 'types/SPM';

type ApprovalContextValue = {
  approvalId: string | undefined;
  approval: SPMApproval | undefined;
  isLoading: boolean;
  isApprovalMode: boolean;
  isOverrideMode: boolean;
  canProposeChanges: boolean;
  /** Whether rejecting here sends the work back to the submitter. Gates the reject button, the row
   * checkboxes, and the row toolbar, so all three agree on one answer. */
  canReject: boolean;
  isApprovalWorkflowsEnabled: boolean;
  proposedChangeGroupId: StringifiedUUID | undefined;
  activeChangeGroupId: StringifiedUUID | undefined;
  triggerPcgId: StringifiedUUID | undefined;
  showProposedChanges: boolean;
  setShowProposedChanges: (show: boolean) => void;
  hasCurrentUserMadeChanges: boolean;
  proposedChangeRows: ProposedChangeWorksheetRow[] | undefined;
  proposedChangeRowCount: number | undefined;
  repeatByContext: SPMApprovalGroup['repeat_by_context'];
  isOverdue: boolean;
  deadlineAt: string | null;
  deadlineTimezone: string | null;
  scopeUserId: StringifiedUUID | undefined;
  /** Set on every repeat-by branch, unlike scopeUserId. Scopes author visibility and nothing else. */
  repeatByAnchorId: StringifiedUUID | undefined;
  scopeUserRowIdsByWorksheet: Record<string, Set<string>>;
  registerScopeUserRowIds: (worksheetId: string, ids: string[]) => void;
  registerVisibleRowIds: (worksheetId: string, rowIds: string[]) => void;
  visibleRowIds: Set<string>;
};

const EMPTY_SET = new Set<string>();

const defaultContextValue: ApprovalContextValue = {
  approvalId: undefined,
  approval: undefined,
  isLoading: false,
  isApprovalMode: false,
  isOverrideMode: false,
  canProposeChanges: true,
  canReject: true,
  isApprovalWorkflowsEnabled: false,
  proposedChangeGroupId: undefined,
  activeChangeGroupId: undefined,
  triggerPcgId: undefined,
  showProposedChanges: false,
  setShowProposedChanges: () => {},
  hasCurrentUserMadeChanges: false,
  proposedChangeRows: undefined,
  proposedChangeRowCount: undefined,
  repeatByContext: null,
  isOverdue: false,
  deadlineAt: null,
  deadlineTimezone: null,
  scopeUserId: undefined,
  repeatByAnchorId: undefined,
  scopeUserRowIdsByWorksheet: {},
  registerScopeUserRowIds: () => {},
  registerVisibleRowIds: () => {},
  visibleRowIds: EMPTY_SET,
};

const ApprovalContext = createContext<ApprovalContextValue>(defaultContextValue);

export const useApprovalContext = () => {
  return useContext(ApprovalContext);
};

export const useProposedChangeRows = (
  worksheetId: StringifiedUUID | undefined,
): ProposedChangeWorksheetRow[] | undefined => {
  const { proposedChangeRows, showProposedChanges } = useApprovalContext();

  return useMemo(() => {
    if (!showProposedChanges || !worksheetId) return undefined;
    return proposedChangeRows?.filter((row) => row.worksheet_id === worksheetId);
  }, [proposedChangeRows, worksheetId, showProposedChanges]);
};

export const useProposedChangeCount = (): number | undefined => {
  const { proposedChangeRows, visibleRowIds } = useApprovalContext();

  return useMemo(() => {
    if (!proposedChangeRows) return undefined;
    if (visibleRowIds.size === 0) return 0;
    return proposedChangeRows
      .filter((row) => visibleRowIds.has(row.row_id))
      .reduce((sum, row) => sum + Object.keys(row.data_changes ?? {}).length, 0);
  }, [proposedChangeRows, visibleRowIds]);
};

export const useCurrentUserHasRejectedRows = (): boolean => {
  const { proposedChangeRows, visibleRowIds } = useApprovalContext();

  return useMemo(() => {
    if (!proposedChangeRows || visibleRowIds.size === 0) return false;
    return proposedChangeRows.some((row) => visibleRowIds.has(row.row_id) && row.review_status === 'rejected');
  }, [proposedChangeRows, visibleRowIds]);
};

/**
 * Context provider for the approval flow.
 *
 * Reads approvalId from URL route params and fetches approval details.
 * Provides the proposedChangeGroupId and toggle state for downstream use.
 */
export const ApprovalContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { approvalId } = useParams<Partial<PlanScenarioApprovalParams>>();
  const isApprovalWorkflowsEnabled = useIsFeatureEnabled('feature_flag_spm_approval_workflows');
  const isApprovalMode = isApprovalWorkflowsEnabled && !!approvalId;

  const [showProposedChanges, setShowProposedChanges] = useState(true);

  // Fetch approval data when approvalId is present and feature flag is enabled
  const approvalQuery = SPMApprovalsService.useGetApprovalQuery(approvalId, {
    enabled: isApprovalMode,
  });

  const approval = approvalQuery.data;
  const proposedChangeGroupId = approval?.approval_group?.object_id;
  const activeChangeGroupId = showProposedChanges ? proposedChangeGroupId : undefined;
  const triggerPcgId = approval?.approval_group?.trigger_pcg_id ?? undefined;

  const proposedChangeRowsQuery = ProposedChangeGroupsServiceV2.useSPMProposedChangeRowsQuery({
    changeGroupId: proposedChangeGroupId,
    options: { enabled: isApprovalMode && !!proposedChangeGroupId },
  });

  // Determine override mode: user has TQ admin access, or is a Plan Admin for this
  // scenario, and is in approval mode but NOT the assigned approver — so they can only
  // act via override.
  const { hasAccess } = usePermissions();
  const hasRbacAccess = hasAccess({ actions: TQ_ACTIONS.TQ_READ });
  const { isPlanAdmin } = useSPMPlanContext();
  const isApprovalPlanAdminEnabled = useIsFeatureEnabled('feature_flag_spm_approval_plan_admin');
  const { query: userQuery } = UsersService.useGetCurrentUserQuery();
  const currentUser = userQuery.data;
  const canOverride = hasRbacAccess || Boolean(isApprovalPlanAdminEnabled && isPlanAdmin);
  const isOverrideMode = canOverride && isApprovalMode && !!approval && approval.approver_id !== currentUser?.id;

  // Whether the approval state allows proposing changes.
  // Consumers should gate on isApprovalMode before consulting this.
  // Group-level resolution takes precedence over individual approval resolution.
  // Always false when the proposed change group has been closed (changes already applied).
  const canProposeChanges = (() => {
    if (approval?.approval_group?.proposed_change_group_status === ProposedChangeGroupStatus.CLOSED) return false;
    if (isOverrideMode) return true;

    const resolvedStatus = approval?.approval_group?.is_approved ?? approval?.is_approved;
    return resolvedStatus === null || resolvedStatus === undefined;
  })();

  // Reject hides unless this step's reject path returns work to an earlier step.
  const approvalGroup = approval?.approval_group;
  const isWorkflowApproval = approvalGroup?.approval_type === 'workflow';
  const isResolved = (approvalGroup?.is_approved ?? approval?.is_approved) != null;
  const { data: workflowRun } = SPMApprovalsService.useGetSPMWorkflowQuery(approvalGroup?.workflow_id ?? undefined, {
    enabled: isApprovalMode && isWorkflowApproval && !!approvalGroup?.workflow_id && !isResolved,
  });

  const canReject = useMemo(() => {
    if (!isWorkflowApproval) return true;
    const template = workflowRun?.definition_template;
    if (!template || !approvalGroup) return false;
    return rejectPathReturnsToStep(findApprovalDefinitionStep(template, workflowRun.approval_nodes, approvalGroup.id));
  }, [isWorkflowApproval, workflowRun, approvalGroup]);

  const repeatByContext = approval?.approval_group?.repeat_by_context ?? null;
  const scopeUserId = repeatByContext?.user_uuid;
  const repeatByAnchorId = approval?.approval_group?.repeat_by_anchor_id ?? undefined;

  const isOverdue = approval?.approval_group?.is_overdue ?? false;
  const deadlineAt = approval?.approval_group?.deadline_at ?? null;
  const deadlineTimezone = approval?.approval_group?.deadline_timezone ?? null;

  const visibleRowsByWorksheet = useRef(new Map<string, Set<string>>());
  const [visibleRowIds, setVisibleRowIds] = useState<Set<string>>(EMPTY_SET);

  const registerVisibleRowIds = useCallback((worksheetId: string, rowIds: string[]) => {
    const map = visibleRowsByWorksheet.current;
    const prev = map.get(worksheetId);
    const next = new Set(rowIds);
    if (prev && prev.size === next.size && [...next].every((id) => prev.has(id))) return;
    map.set(worksheetId, next);
    const merged = new Set<string>();
    for (const ids of map.values()) {
      for (const id of ids) merged.add(id);
    }
    setVisibleRowIds(merged);
  }, []);

  const [scopeUserRowIdsByWorksheet, setScopeUserRowIdsByWorksheet] = useState<Record<string, Set<string>>>({});

  // Scope rows belong to one (approval, scope user) pair. Without this reset the previous
  // selection's entries stay applied until each worksheet's next response lands, so switching the
  // reviewed user briefly greys rows by the wrong person's scope. Clearing degrades to "no scope
  // known yet" — the same state as a fresh load — rather than to a confidently wrong one.
  useEffect(() => {
    setScopeUserRowIdsByWorksheet((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [approvalId, scopeUserId]);

  const registerScopeUserRowIds = useCallback((worksheetId: string, ids: string[]) => {
    setScopeUserRowIdsByWorksheet((prev) => {
      const prevSet = prev[worksheetId];
      const nextSet = new Set(ids);
      if (prevSet && prevSet.size === nextSet.size && [...nextSet].every((id) => prevSet.has(id))) return prev;
      return { ...prev, [worksheetId]: nextSet };
    });
  }, []);

  const proposedChangeRows = proposedChangeRowsQuery.data?.data;
  const proposedChangeRowCount = proposedChangeRowsQuery.data?.total_count;

  const hasCurrentUserMadeChanges = useMemo(() => {
    if (!currentUser?.id || !proposedChangeRows) return false;
    return proposedChangeRows.some((row) => row.last_modified_by === currentUser.id);
  }, [proposedChangeRows, currentUser?.id]);

  const value = useMemo((): ApprovalContextValue => {
    return {
      approvalId,
      approval,
      isLoading: approvalQuery.isLoading,
      isApprovalMode,
      isOverrideMode,
      canProposeChanges,
      canReject,
      isApprovalWorkflowsEnabled,
      proposedChangeGroupId,
      activeChangeGroupId,
      triggerPcgId,
      showProposedChanges,
      setShowProposedChanges,
      hasCurrentUserMadeChanges,
      proposedChangeRows,
      proposedChangeRowCount,
      repeatByContext,
      isOverdue,
      deadlineAt,
      deadlineTimezone,
      scopeUserId,
      repeatByAnchorId,
      scopeUserRowIdsByWorksheet,
      registerScopeUserRowIds,
      registerVisibleRowIds,
      visibleRowIds,
    };
  }, [
    approvalId,
    approval,
    approvalQuery.isLoading,
    isApprovalMode,
    isOverrideMode,
    canProposeChanges,
    canReject,
    isApprovalWorkflowsEnabled,
    proposedChangeGroupId,
    activeChangeGroupId,
    triggerPcgId,
    showProposedChanges,
    hasCurrentUserMadeChanges,
    proposedChangeRows,
    proposedChangeRowCount,
    repeatByContext,
    isOverdue,
    deadlineAt,
    deadlineTimezone,
    scopeUserId,
    repeatByAnchorId,
    scopeUserRowIdsByWorksheet,
    registerScopeUserRowIds,
    registerVisibleRowIds,
    visibleRowIds,
  ]);

  return <ApprovalContext.Provider value={value}>{children}</ApprovalContext.Provider>;
};
